import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Sparkles } from "lucide-react";

type Insight = { id: string; kind: string; title: string; body: string | null; created_at: string };

export default function CreatorAI() {
  const { user } = useAuth();
  const [items, setItems] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("ai_insights").select("*").eq("creator_id", user.id).order("created_at", { ascending: false });
      setItems((data as Insight[]) || []);
      setLoading(false);
    })();
  }, [user]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold">AI Hype Engine</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {loading ? <p className="text-center text-muted-foreground py-12">Loading…</p>
          : items.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Sparkles className="size-10 mx-auto mb-2" />
              No insights yet. The AI engine will surface growth signals here as your activity grows.
            </CardContent></Card>
          ) : items.map((i) => (
            <Card key={i.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{i.title}</CardTitle>
                  <Badge variant="outline" className="capitalize">{i.kind}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {i.body && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{i.body}</p>}
                <p className="text-[10px] text-muted-foreground mt-2">{new Date(i.created_at).toLocaleString()}</p>
              </CardContent>
            </Card>
          ))}
      </main>
    </div>
  );
}
